import { mdiGavel, mdiSyncAlert } from '@mdi/js';

import { stableStringify } from '../stable-stringify';

export type AutomationWorkerId = 'rejection' | 'denial';
export type RuleActionType =
    | 'CLOSE_OUT'
    | 'SWITCH_TO_PRIVATE_PAY'
    | 'CREATE_APPEAL'
    | 'CREATE_MESSAGE'
    | 'EXECUTE_ENTER_FUNCTION';

/**
 * Extensible per-match scope.
 * Known values: TRANSMISSION_STATUS, PAYMENT, PAYER, BILLING_PROVIDER,
 * RENDERING_PROVIDER, FACILITY, PROCEDURE_CODE, DATE_OF_SERVICE.
 */
export type RuleMatchScope = string;

/** Worker default scope used when seeding new rules/match blocks. */
export type WorkerDefaultScope = 'TRANSMISSION_STATUS' | 'PAYMENT';

export type ConditionKind =
    | 'billingProviderId'
    | 'carcCode'
    | 'carcGroup'
    | 'contains'
    | 'facilityId'
    | 'payerId'
    | 'procedureCode'
    | 'rarcCode'
    | 'renderingProviderId';

export interface TransmissionMatchValue {
    contains: string[];
}

export interface PaymentMatchValue {
    carc: { codes: string[]; group: string[] };
    rarc: { codes: string[] };
}

export interface PayerMatchValue {
    payerIds: string[];
}

export interface BillingProviderMatchValue {
    billingProviderIds: string[];
}

export interface RenderingProviderMatchValue {
    renderingProviderIds: string[];
}

export interface FacilityMatchValue {
    facilityIds: string[];
}

export interface ProcedureCodeMatchValue {
    procedureCodes: string[];
}

export interface DateOfServiceMatchValue {
    fromDos: string | null;
    throughDos: string | null;
}

export type RuleMatchValue =
    | BillingProviderMatchValue
    | DateOfServiceMatchValue
    | FacilityMatchValue
    | PaymentMatchValue
    | PayerMatchValue
    | ProcedureCodeMatchValue
    | RenderingProviderMatchValue
    | TransmissionMatchValue;

export interface RuleMatch {
    /** When true, a claim matching any listed value SKIPS this rule. Omitted/false = ordinary inclusion. */
    exclude?: boolean;
    scope: RuleMatchScope;
    value: RuleMatchValue;
    /**
     * Client-side identity of ONE block, assigned when the draft is hydrated and when a block is
     * added. The wire format has no block id, so without this a block can only be recognised by its
     * values — and then a block added in this session inherits the legacy exemption of an identical
     * block the same session deleted (`getCarcPairRequirements`).
     *
     * Never persisted: `normalizeAutomationRuleForPersist` strips it, and `automationWorkersEqual`
     * ignores it so re-adding identical values still reads as no change.
     */
    uid?: string;
}

export interface ReconsiderationDocument {
    documentId: string;
    name: string;
}

/** A coder rule the account can disable per-claim from an EXECUTE_ENTER_FUNCTION action. */
export interface CoderRuleSummary {
    description: string;
    isActive: boolean;
    ruleNumber: number;
}

export interface RuleAction {
    type: RuleActionType;
    value: Record<string, unknown>;
}

export interface AutomationRule {
    action: RuleAction;
    /** Group tasks created by this rule under one shared message. */
    group?: boolean;
    id: string;
    match: RuleMatch[];
    name: string;
    priority?: number;
}

export interface AutomationWorker {
    desc: string;
    icon: string;
    id: AutomationWorkerId;
    key: string;
    name: string;
    rules: AutomationRule[];
    scope: WorkerDefaultScope;
    scopeLabel: string;
}

export interface CatalogCode {
    code: string;
    count?: number;
    desc: string;
}

export interface CatalogGroup {
    count: number;
    group: string;
}

export interface CatalogPayer {
    meta: string;
    name: string;
    payerId: string;
}

export interface CatalogProvider {
    meta: string;
    name: string;
    providerId: string;
}

export interface CatalogFacility {
    facilityId: string;
    meta: string;
    name: string;
}

export interface CatalogDocument {
    documentId: string;
    name: string;
}

export interface EnterCatalog {
    carcCodes: CatalogCode[];
    carcGroups: CatalogGroup[];
    documents: CatalogDocument[];
    payers: CatalogPayer[];
    rarcCodes: CatalogCode[];
    /** Transmission status messages from reporting (message + occurrence count). */
    rejectionStatuses: CatalogCode[];
}

export interface CatalogUsage {
    count: number;
    ruleNames: string[];
    ruleNumbers: number[];
}

export interface UsageBadgePresentation {
    label: string;
    notUsed: boolean;
    short: string;
    title: string;
    used: boolean;
}

export const MESSAGE_CATEGORIES = [
    'PAYER_FOLLOWUP',
    'PROVIDER_FOLLOWUP',
    'CODING_REVIEW',
    'RECORDS_REQUEST',
    'AUTHORIZATION',
    'GENERAL',
] as const;

export type MessageCategory = (typeof MESSAGE_CATEGORIES)[number];

export const AUTOMATION_WORKER_ICONS: Record<AutomationWorkerId, string> = {
    rejection: mdiSyncAlert,
    denial: mdiGavel,
};

export function cloneAutomationWorkers(
    workers: Record<AutomationWorkerId, AutomationWorker>,
): Record<AutomationWorkerId, AutomationWorker> {
    return JSON.parse(JSON.stringify(workers)) as Record<AutomationWorkerId, AutomationWorker>;
}

/** A new `RuleMatch.uid`. Mirrors the rule-id generator: `crypto` first, timestamp when it is absent. */
export function createMatchUid(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return `match-${crypto.randomUUID()}`;
    }

    return `match-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/** Blocks without their `uid` — for the save payload, and for any comparison against saved state. */
export function stripMatchUids(matches: RuleMatch[] | undefined): RuleMatch[] {
    return (matches ?? []).map(({ uid, ...match }) => match);
}

/**
 * The same workers with every block `uid` dropped, so identity never reaches a comparison.
 * Shallow on purpose — this runs on each keystroke through `automationWorkersEqual`.
 */
export function withoutMatchUids(
    workers: Record<AutomationWorkerId, AutomationWorker>,
): Record<AutomationWorkerId, AutomationWorker> {
    return Object.fromEntries(
        Object.entries(workers ?? {}).map(([id, worker]) => [
            id,
            {
                ...worker,
                rules: (worker?.rules ?? []).map(rule => ({ ...rule, match: stripMatchUids(rule.match) })),
            },
        ]),
    ) as Record<AutomationWorkerId, AutomationWorker>;
}

export function automationWorkersEqual(
    left: Record<AutomationWorkerId, AutomationWorker>,
    right: Record<AutomationWorkerId, AutomationWorker>,
): boolean {
    // uid-blind on purpose: deleting a block and re-adding the same values is still "no change".
    return stableStringify(withoutMatchUids(left)) === stableStringify(withoutMatchUids(right));
}
